<script lang="ts">
import { defineComponent } from "vue";
import type { PropType, ConferenceVotingRound, Locale } from "@/types";
import ItemSummaryView from '@/components/backend/ItemSummaryView.vue';

export interface ContestSelectionListContest {
  id: number;
  title: Record<string, string>;
  reference: string;
  label: string;
  description: Record<string, string>;
  options_count: number;
}

export interface VotingRoundItem {
  id: number;
  votingRoundId: number;
  itemReference: string;
  itemType: "Contest" | "InformationPage";
  position: number;
}

export interface InformationPage {
  id: number;
  reference: string;
  title: Record<string, string>;
  content: Record<string, string>;
}

export interface SelectableItem {
  reference: string;
  itemType: "Contest" | "InformationPage";
  label: string;
  title: Record<string, string>;
  optionsCount: number;
}

interface SelectedItemKey {
  reference: string;
  itemType: "Contest" | "InformationPage";
}

export default defineComponent({
  name: "ItemSelectionList",
  components: { ItemSummaryView },
  props: {
    scope: {
      type: String,
      required: true,
    },
    contests: {
      type: Array as PropType<ContestSelectionListContest[]>,
      required: true,
    },
    locked: {
      type: Boolean,
      default: false,
    },
    enabledLocales: {
      type: Array as PropType<Locale[]>,
      required: true,
    },
    currentLocale: {
      type: String,
      required: true,
    },
    votingRoundItems: {
      type: Array as PropType<VotingRoundItem[]>,
      required: true,
    },
    informationPages: {
      type: Array as PropType<InformationPage[]>,
      default: () => [],
    }
  },
  data({ votingRoundItems }) {
    return {
      wasValidated: false,
      selectedItems: votingRoundItems.map((vri: VotingRoundItem) => ({
        reference: vri.itemReference,
        itemType: vri.itemType,
      })) as SelectedItemKey[],
      errors: {
        contests: null,
      },
      contestFilter: "",
      debounceId: null,
      isHandRaise: false,
      elNodeObserver: null,
      el: null,
    }
  },
  watch: {
    isHandRaise: function(newState) {
      if (newState) {
        this.selectedItems = this.selectedItems.slice(0, 1);
      }
    }
  },
  computed: {
    contestFilterDebouncer: {
      get() {
        return this.contestFilter;
      },
      set(newValue: string) {
        this.debounce(() => this.contestFilter = newValue, 500)
      }
    },
    allAvailableItems(): SelectableItem[] {
      const contestItems: SelectableItem[] = this.contests.map((c: ContestSelectionListContest) => ({
        reference: c.reference,
        itemType: "Contest" as const,
        label: c.label,
        title: c.title,
        optionsCount: c.options_count,
      }));
      const pageItems: SelectableItem[] = (this.informationPages || []).map((p: InformationPage) => ({
        reference: p.reference,
        itemType: "InformationPage" as const,
        label: p.title?.[this.displayLocale] || p.reference,
        title: p.title || {},
        optionsCount: 0,
      }));
      return [...contestItems, ...pageItems];
    },
    selectedItemKeys(): Set<string> {
      return new Set(this.selectedItems.map((i: SelectedItemKey) => this.itemKey(i)));
    },
    filteredItems(): SelectableItem[] {
      if (this.contestFilter === "") return this.allAvailableItems;

      const normalisedFilterValue = this.contestFilter.toLowerCase();
      return this.allAvailableItems.filter((item: SelectableItem) => {
        if (this.selectedItemKeys.has(`${item.itemType}:${item.reference}`)) return true;

        const searchedValue = item.label.toLowerCase() + (item.title[this.displayLocale] || "").toLowerCase();
        return searchedValue.includes(normalisedFilterValue);
      });
    },
    displayLocale() {
      return this.enabledLocales.includes(this.currentLocale) ? this.currentLocale : this.enabledLocales[0];
    },
    selectedQty() {
      return this.selectedItems.length;
    },
    deselectedPersistedItems(): VotingRoundItem[] {
      return this.votingRoundItems.filter(
        (vri: VotingRoundItem) => !this.selectedItemKeys.has(`${vri.itemType}:${vri.itemReference}`)
      );
    },
  },
  methods: {
    debounce(action: Function, delay: number = 1000) {
      if (this.debounceId) clearTimeout(this.debounceId);
      this.debounceId = setTimeout(action, delay);
    },
    errorMessagesFor(attribute: string) {
      return (this.errors[attribute] || []).map((e: string) => this.errorMessage(e)).join(" ");
    },
    errorMessage(error: string) {
      if (this.$te(`js.backend.voting_round_modal.errors.messages.${error}`))
        return this.$t(`js.backend.voting_round_modal.errors.messages.${error}`);
      else
        return error;
    },
    itemKey(item: SelectableItem | SelectedItemKey): string {
      return `${item.itemType}:${item.reference}`;
    },
    isItemSelected(item: SelectableItem): boolean {
      return this.selectedItemKeys.has(this.itemKey(item));
    },
    itemPosition(item: SelectableItem): number {
      const index = this.selectedItems.findIndex(
        (i: SelectedItemKey) => i.reference === item.reference && i.itemType === item.itemType
      );
      return index === -1 ? NaN : index + 1;
    },
    handleItem(reference: string, itemType: string) {
      const idx = this.selectedItems.findIndex(
        (i: SelectedItemKey) => i.reference === reference && i.itemType === itemType
      );
      if (idx !== -1) {
        this.selectedItems.splice(idx, 1);
      } else {
        this.selectedItems.push({ reference, itemType: itemType as "Contest" | "InformationPage" });
      }
    },
    handleItemPosition(reference: string, itemType: string, newPosition: number) {
      const idx = this.selectedItems.findIndex(
        (i: SelectedItemKey) => i.reference === reference && i.itemType === itemType
      );
      const item = this.selectedItems[idx];
      const newIndex = Math.min(Math.max(1, newPosition), this.selectedQty) - 1;
      this.selectedItems.splice(idx, 1);
      this.selectedItems.splice(newIndex, 0, item);
    },
    setError(attribute: string, type: string) {
      Object.assign(this.errors, {
        [attribute]: [...(this.errors[attribute] || []), type]
      });
    },
    selectAll() {
      for (const item of this.allAvailableItems) {
        if (!this.selectedItemKeys.has(this.itemKey(item))) {
          this.selectedItems.push({ reference: item.reference, itemType: item.itemType });
        }
      }
    },
    deselectAll() {
      this.selectedItems = [];
    },
    toggleHandraise() {
      if (this.el.attributes["data-handraise"]) this.isHandRaise = true;
      else this.isHandRaise = false;
    },
  },
  mounted() {
    this.el = document.querySelector("[data-vue-component=item-selection-list]");
    this.elNodeObserver = new MutationObserver(this.toggleHandraise);
    const config = { attributes: true };
    this.elNodeObserver.observe(this.el, config);
  },
  beforeUnmount() {
    this.elNodeObserver.disconnect();
  },
});
</script>

<template>
  <div class="mb-3">
    <label class="form-label">
      <span v-text="$t('js.backend.voting_round_modal.contests_title')" />
    </label>
    <div v-if="isHandRaise" class="alert alert-warning">
      <AVIcon icon="exclamation-triangle" class="me-1" />
      {{ $t("js.backend.item_selection_list.hand_raise_warning") }}
    </div>
    <div class="input-group mb-3 w-50">
      <span class="input-group-text"><AVIcon icon="search" /></span>
      <input class="form-control" type="search" v-model="contestFilterDebouncer" @keydown.enter.prevent>
    </div>
    <div class="row">
      <div class="col-6 hstack">
        <p>{{ $t("js.backend.voting_round_modal.available_contests") }}</p>
        <button type="button" class="btn btn-light btn-sm ms-auto border-secondary mb-3" @click.prevent="selectAll"
                v-text="$t('js.backend.voting_round_modal.select_all_btn')" :disabled="selectedQty === allAvailableItems.length || isHandRaise || locked" />
      </div>
      <div class="col-6 hstack">
        <p>{{ $t("js.backend.voting_round_modal.selected_contests") }}</p>
        <button type="button" class="btn btn-light btn-sm  ms-auto border-secondary mb-3" @click.prevent="deselectAll"
                v-text="$t('js.backend.voting_round_modal.deselect_all_btn')" :disabled="selectedQty === 0 || locked" />
      </div>
    </div>

    <template v-for="(item, index) in selectedItems" :key="`hidden-${item.itemType}-${item.reference}`">
      <input type="hidden" :name="`${scope}[voting_round_items_attributes][${index}][item_reference]`" :value="item.reference">
      <input type="hidden" :name="`${scope}[voting_round_items_attributes][${index}][item_type]`" :value="item.itemType">
    </template>

    <template v-for="(vri, destroyIndex) in deselectedPersistedItems" :key="`destroy-${vri.itemType}-${vri.itemReference}`">
      <input type="hidden" :name="`${scope}[voting_round_items_attributes][${selectedItems.length + destroyIndex}][id]`" :value="vri.id">
      <input type="hidden" :name="`${scope}[voting_round_items_attributes][${selectedItems.length + destroyIndex}][_destroy]`" value="1">
    </template>


    <TransitionGroup name="selection-list" tag="div" class="item-selection-list" :class="{ 'is-invalid': errors.contests }">
      <ItemSummaryView
          v-for="item in filteredItems"
          :item="item"
          :editMode="locked"
          :isSelected="isItemSelected(item)"
          :key="itemKey(item)"
          :position="itemPosition(item)"
          :selectedQty="selectedQty"
          :disabled="isHandRaise && selectedQty === 1"
          :locked="locked"
          :isHandRaise="isHandRaise"
          @handleItem="handleItem"
          @handleItemPosition="handleItemPosition"
      />
    </TransitionGroup>
    <div class="invalid-feedback" v-if="errors.contests" v-text="errorMessagesFor('contests')" />
  </div>
</template>

<style lang="css" scoped>
.selection-list-move, /* apply transition to moving elements */
.selection-list-enter-active,
.selection-list-leave-active {
  transition: all 0.5s ease;
}

.selection-list-move {
}

.selection-list-enter-from,
.selection-list-leave-to {
  transform: translateX(100%);
  opacity: 0.0;
}

.selection-list-leave-active {
  position: absolute;
  width: calc(50% - 1.75rem);
  height: 4rem;
}

.item-selection-list {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1.25rem;
  grid-auto-flow: column;
  grid-auto-rows: 4rem;
  overflow-x: clip;
  overflow-y: auto;
}
</style>
